// /consent — Public patient consent-for-services form.
//
// Includes:
//   • Full mental-health informed-consent copy
//   • Patient + emergency contact + insurance fields
//   • Both signature modes: typed name AND drawn on-canvas
//   • Downloads a signed PDF after submission (client keeps a copy)
//   • Submission stored in MongoDB and viewable by admin

import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { ArrowLeft, Check, PenLine, RotateCcw, Download, Loader2 } from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSite } from "../context/SiteContext";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────
// Consent copy — standard mental-health informed consent.
// Editable later via /admin/editor if you swap to site content.
// ─────────────────────────────────────────────────────────────
const CONSENT_SECTIONS = [
  {
    title: "Nature of services",
    body:
      "Let Go Wellness provides outpatient mental-health services including individual, couples, family, and group therapy delivered in-person and via secure telehealth. Sessions are typically 50 minutes; couples and initial evaluations may run 80 minutes. Care is delivered by licensed clinicians and, at times, by supervised trainees under a licensed clinical supervisor. There are no guarantees regarding treatment outcomes. Therapy may bring up strong emotions, memories, or discomfort as part of the healing process.",
  },
  {
    title: "Confidentiality & its legal limits",
    body:
      "All communication and records are confidential and protected under HIPAA. However, Connecticut law and professional ethics require your clinician to break confidentiality in the following circumstances: (1) reasonable suspicion of abuse or neglect of a child, elder, or dependent adult; (2) an imminent, serious threat of harm to yourself or another identifiable person; (3) a court order compelling disclosure. Consultation with fellow clinicians and clinical supervisors regarding your care is standard practice and remains confidential.",
  },
  {
    title: "Fees, payment & insurance",
    body:
      "Session fees are due at the time of service. You are financially responsible for services rendered regardless of insurance status. If Let Go Wellness is in-network with your insurance, we bill directly and you are responsible for co-pays, deductibles, and any denied claims. If you are self-pay or out-of-network, a superbill can be provided on request. Returned-check and outstanding-balance fees may apply.",
  },
  {
    title: "Cancellation policy",
    body:
      "We ask for at least 24 hours' notice for cancellations. Sessions cancelled with less than 24 hours' notice, or missed without notice, are billed at the full session fee. Insurance does not reimburse for late cancellations. Repeated no-shows may result in discharge from services.",
  },
  {
    title: "Communication & telehealth",
    body:
      "Email, text messaging, and standard phone are not fully secure and are used only for scheduling and brief logistics — never for clinical content. For clinical exchange we use a HIPAA-compliant telehealth and messaging platform. Telehealth sessions require a private space and a reliable internet connection; both patient and clinician must be physically located in the state of Connecticut at the time of the session unless otherwise agreed.",
  },
  {
    title: "Emergencies & after-hours care",
    body:
      "Let Go Wellness is not an emergency service and does not provide 24-hour crisis coverage. If you are in crisis and cannot wait for a scheduled appointment, call 988 (Suicide & Crisis Lifeline), 911, or go to your nearest emergency room. You may leave a non-urgent voicemail; we return calls within one business day.",
  },
  {
    title: "Client rights",
    body:
      "You have the right to ask questions about your treatment, refuse or discontinue services at any time, request a copy of your record, and request a second opinion or transfer to another provider. Complaints regarding care may be submitted to the Connecticut Department of Public Health, Practitioner Licensing and Investigations Section, 410 Capitol Ave., Hartford, CT 06134.",
  },
  {
    title: "Consent",
    body:
      "By signing below, you acknowledge that you have read, understood, and had opportunity to ask questions about the terms above, and you consent to receive mental-health services from Let Go Wellness. If the patient is a minor (under 18) or under conservatorship, a parent, legal guardian, or conservator must also sign.",
  },
];

// ─────────────────────────────────────────────────────────────
// Signature Pad (hand-rolled canvas — no external deps)
// ─────────────────────────────────────────────────────────────
function SignaturePad({ padRef, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1C1917";
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPoint(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) {
      setHasInk(true);
      onChange?.(true);
    }
  };
  const end = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange?.(false);
  };

  // Expose the "get data URL" and "clear" via padRef
  useEffect(() => {
    if (!padRef) return;
    padRef.current = {
      toDataURL: () => (hasInk ? canvasRef.current.toDataURL("image/png") : ""),
      clear,
      hasInk: () => hasInk,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInk]);

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        className="w-full h-40 border border-[#D6CFC2] bg-white cursor-crosshair touch-none"
      />
      <button
        type="button"
        onClick={clear}
        data-testid="signature-clear"
        className="absolute top-2 right-2 inline-flex items-center gap-1 text-xs text-[#86827B] hover:text-[#9F5C45]"
      >
        <RotateCcw className="w-3 h-3" /> Clear
      </button>
      {!hasInk && (
        <p className="absolute inset-0 flex items-center justify-center text-[#B8B0A1] italic text-sm pointer-events-none">
          Sign here with your finger or mouse
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main page component
// ─────────────────────────────────────────────────────────────
export default function Consent() {
  const { site } = useSite();
  const brand = site?.brand_name || "Let Go Wellness";
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromEnroll = params.get("from") === "enroll";

  const [form, setForm] = useState({
    patient_name: "",
    patient_dob: "",
    patient_address: "",
    patient_phone: "",
    patient_email: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relationship: "",
    insurance_provider: "",
    insurance_member_id: "",
    typed_signature: "",
    minor_guardian_name: "",
    agreed_terms: false,
    agreed_communications: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const padRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  const update = (k) => (e) =>
    setForm((prev) => ({
      ...prev,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.agreed_terms) {
      toast.error("Please check the box to agree to the consent terms.");
      return;
    }
    if (!form.typed_signature.trim()) {
      toast.error("Please type your full legal name as your e-signature.");
      return;
    }
    if (!hasInk) {
      toast.error("Please also draw your signature in the pad.");
      return;
    }
    setBusy(true);
    const t = toast.loading("Filing your consent form…");
    try {
      const drawn_signature_b64 = padRef.current?.toDataURL() || "";
      const res = await axios.post(`${API_BASE}/consent-forms/submit`, {
        ...form,
        drawn_signature_b64,
      });
      setDone(res.data);
      toast.success("Consent received — you can download your signed copy below.", { id: t });
      // Scroll to top so they see the receipt
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || err.message || "Something went wrong.";
      toast.error(msg, { id: t });
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = () => {
    if (!done) return;
    const pdf = new jsPDF({ unit: "in", format: "letter" });
    const W = 8.5;
    const marginX = 0.7;
    let y = 0.8;

    // Header
    pdf.setFont("times", "bold");
    pdf.setFontSize(20);
    pdf.text(brand, marginX, y);
    y += 0.3;
    pdf.setFont("times", "italic");
    pdf.setFontSize(11);
    pdf.setTextColor(120, 115, 105);
    pdf.text("Informed Consent for Mental-Health Services", marginX, y);
    pdf.setTextColor(28, 25, 23);
    y += 0.35;

    // Divider
    pdf.setDrawColor(180, 170, 150);
    pdf.setLineWidth(0.005);
    pdf.line(marginX, y, W - marginX, y);
    y += 0.3;

    // Patient info block
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    const row = (label, value) => {
      pdf.setFont("helvetica", "bold");
      pdf.text(label.toUpperCase(), marginX, y);
      pdf.setFont("helvetica", "normal");
      pdf.text(value || "—", marginX + 1.8, y);
      y += 0.22;
    };
    row("Patient name", done.patient_name);
    row("Date of birth", done.patient_dob);
    row("Address", done.patient_address);
    row("Phone", done.patient_phone);
    row("Email", done.patient_email);
    if (done.emergency_contact_name)
      row(
        "Emergency contact",
        `${done.emergency_contact_name} (${done.emergency_contact_relationship || "Contact"}) — ${done.emergency_contact_phone}`
      );
    if (done.insurance_provider)
      row("Insurance", `${done.insurance_provider}${done.insurance_member_id ? " · " + done.insurance_member_id : ""}`);
    if (done.minor_guardian_name) row("Guardian", done.minor_guardian_name);
    row("Signed at", new Date(done.signed_at).toLocaleString());
    y += 0.2;

    // Consent copy
    pdf.setDrawColor(180, 170, 150);
    pdf.line(marginX, y, W - marginX, y);
    y += 0.3;

    for (const section of CONSENT_SECTIONS) {
      if (y > 10) {
        pdf.addPage();
        y = 0.8;
      }
      pdf.setFont("times", "bold");
      pdf.setFontSize(11);
      pdf.text(section.title, marginX, y);
      y += 0.2;
      pdf.setFont("times", "normal");
      pdf.setFontSize(10);
      const lines = pdf.splitTextToSize(section.body, W - 2 * marginX);
      pdf.text(lines, marginX, y);
      y += 0.16 * lines.length + 0.2;
    }

    // Signature block
    if (y > 9.5) {
      pdf.addPage();
      y = 0.8;
    }
    y += 0.2;
    pdf.setDrawColor(180, 170, 150);
    pdf.line(marginX, y, W - marginX, y);
    y += 0.3;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text("PATIENT SIGNATURE", marginX, y);
    y += 0.25;
    pdf.setFont("times", "italic");
    pdf.setFontSize(14);
    pdf.text(done.typed_signature || "", marginX, y);
    y += 0.2;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(120, 115, 105);
    pdf.text(`Signed electronically on ${new Date(done.signed_at).toLocaleString()}`, marginX, y);
    pdf.setTextColor(28, 25, 23);
    y += 0.35;

    // Drawn signature image (from server)
    if (done.drawn_signature_url) {
      const url = done.drawn_signature_url.startsWith("http")
        ? done.drawn_signature_url
        : `${process.env.REACT_APP_BACKEND_URL}${done.drawn_signature_url}`;
      // Since we already have the drawn signature captured on the canvas, embed from padRef
      const dataUrl = padRef.current?.toDataURL();
      if (dataUrl) {
        try {
          pdf.addImage(dataUrl, "PNG", marginX, y, 3.0, 1.0);
        } catch (err) {
          console.warn("Could not embed signature in PDF:", err);
        }
      }
      void url;
    }

    pdf.save(`consent-${(done.patient_name || "patient").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`);
  };

  // Success view — shown after submission
  if (done) {
    return (
      <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased" data-testid="consent-success">
        <Navbar />
        <section className="pt-48 pb-24">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#3A5A40]/10 flex items-center justify-center mb-6">
              <Check className="w-8 h-8 text-[#3A5A40]" />
            </div>
            <p className="overline text-[#9F5C45] mb-3">Received & filed</p>
            <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight mb-4">
              Thank you, {done.patient_name.split(" ")[0]}.
            </h1>
            <p className="text-[#57534E] max-w-xl mx-auto mb-10">
              Your consent for services has been received. A copy is stored securely and available
              to your care team. Please download your own signed copy below for your records.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={downloadPdf}
                data-testid="consent-download-pdf"
                className="inline-flex items-center gap-2 px-6 py-3 btn-primary text-xs uppercase tracking-[0.22em]"
              >
                <Download className="w-4 h-4" /> Download signed PDF
              </button>
              <button
                onClick={() => navigate("/")}
                data-testid="consent-back-home"
                className="inline-flex items-center gap-2 px-6 py-3 btn-ghost text-xs uppercase tracking-[0.22em]"
              >
                Back to site
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased" data-testid="consent-page">
      <Navbar />
      <section className="pt-48 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <Link
            to={fromEnroll ? "/enroll" : "/"}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#9F5C45] hover:text-[#3A5A40]"
            data-testid="consent-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <p className="overline text-[#9F5C45] mt-6 mb-3">Informed consent</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1C1917] mb-4">
            Consent for <em className="text-[#3A5A40]">services.</em>
          </h1>
          <p className="text-[#57534E] max-w-2xl">
            Please read each section carefully. When you&apos;re ready, complete your details and sign below.
            You&apos;ll receive a downloadable copy of your signed form immediately after submitting.
          </p>

          {/* Consent copy sections */}
          <div className="mt-12 space-y-8">
            {CONSENT_SECTIONS.map((s) => (
              <div key={s.title} className="border border-[#E2DCD0] bg-white p-6" data-testid={`consent-section-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <p className="overline text-[#9F5C45] mb-2">{s.title}</p>
                <p className="text-[#1C1917] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={submit} className="mt-16 space-y-10" data-testid="consent-form">
            {/* Patient info */}
            <fieldset className="grid md:grid-cols-2 gap-6">
              <legend className="overline text-[#9F5C45] mb-4 block col-span-full">Patient information</legend>
              <Input label="Full legal name" required value={form.patient_name} onChange={update("patient_name")} testId="patient_name" />
              <Input label="Date of birth" placeholder="MM / DD / YYYY" value={form.patient_dob} onChange={update("patient_dob")} testId="patient_dob" />
              <Input label="Address" className="md:col-span-2" value={form.patient_address} onChange={update("patient_address")} testId="patient_address" />
              <Input label="Phone" type="tel" required value={form.patient_phone} onChange={update("patient_phone")} testId="patient_phone" />
              <Input label="Email" type="email" required value={form.patient_email} onChange={update("patient_email")} testId="patient_email" />
            </fieldset>

            {/* Emergency contact */}
            <fieldset className="grid md:grid-cols-3 gap-6">
              <legend className="overline text-[#9F5C45] mb-4 block col-span-full">Emergency contact</legend>
              <Input label="Name" value={form.emergency_contact_name} onChange={update("emergency_contact_name")} testId="emergency_contact_name" />
              <Input label="Phone" type="tel" value={form.emergency_contact_phone} onChange={update("emergency_contact_phone")} testId="emergency_contact_phone" />
              <Input label="Relationship" placeholder="e.g. spouse, parent" value={form.emergency_contact_relationship} onChange={update("emergency_contact_relationship")} testId="emergency_contact_relationship" />
            </fieldset>

            {/* Insurance (optional) */}
            <fieldset className="grid md:grid-cols-2 gap-6">
              <legend className="overline text-[#9F5C45] mb-4 block col-span-full">Insurance <span className="text-[#86827B] font-normal normal-case tracking-normal text-xs">(optional)</span></legend>
              <Input label="Provider" value={form.insurance_provider} onChange={update("insurance_provider")} testId="insurance_provider" />
              <Input label="Member ID" value={form.insurance_member_id} onChange={update("insurance_member_id")} testId="insurance_member_id" />
            </fieldset>

            {/* Minor / guardian */}
            <fieldset>
              <Input label="If the patient is a minor (under 18), guardian name" value={form.minor_guardian_name} onChange={update("minor_guardian_name")} testId="minor_guardian_name" />
            </fieldset>

            {/* Agreements */}
            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.agreed_terms}
                  onChange={update("agreed_terms")}
                  data-testid="agree-terms"
                  className="mt-1 w-4 h-4 accent-[#3A5A40]"
                />
                <span className="text-sm text-[#1C1917]">
                  I have read, understood, and agree to all the terms above and consent to receive
                  mental-health services from {brand}. <span className="text-[#9F5C45]">Required.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.agreed_communications}
                  onChange={update("agreed_communications")}
                  data-testid="agree-communications"
                  className="mt-1 w-4 h-4 accent-[#3A5A40]"
                />
                <span className="text-sm text-[#57534E]">
                  I consent to be contacted by phone, text, and email for scheduling and non-clinical
                  logistics. <span className="text-[#86827B]">Optional.</span>
                </span>
              </label>
            </div>

            {/* Signature */}
            <fieldset className="space-y-6">
              <legend className="overline text-[#9F5C45] mb-4 block">
                <PenLine className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" /> Signature
              </legend>
              <Input
                label="Type your full legal name as e-signature"
                required
                value={form.typed_signature}
                onChange={update("typed_signature")}
                testId="typed_signature"
                fontStyle="italic"
              />
              <div>
                <p className="overline text-[#57534E] mb-2 block">Also draw your signature</p>
                <SignaturePad padRef={padRef} onChange={setHasInk} />
              </div>
            </fieldset>

            {/* Submit */}
            <div className="border-t border-[#E2DCD0] pt-6">
              <button
                type="submit"
                disabled={busy}
                data-testid="consent-submit"
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 btn-primary text-xs uppercase tracking-[0.22em] disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Filing…
                  </>
                ) : (
                  <>
                    <PenLine className="w-4 h-4" /> Sign & submit consent
                  </>
                )}
              </button>
              <p className="text-xs text-[#86827B] mt-3">
                By signing you&apos;re creating a legally binding electronic signature per the E-SIGN Act.
              </p>
            </div>
          </form>
        </div>
      </section>
      <Footer />
    </main>
  );
}

// ─────────────────────────────────────────────────────────────
function Input({ label, className = "", required, testId, fontStyle, ...rest }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline text-[#57534E] mb-2 block">
        {label} {required && <span className="text-[#9F5C45]">*</span>}
      </span>
      <input
        required={required}
        data-testid={`consent-input-${testId}`}
        style={fontStyle ? { fontStyle } : undefined}
        className="w-full border-0 border-b-2 border-[#D6CFC2] bg-transparent px-1 py-3 text-base focus:outline-none focus:border-[#3A5A40] transition-colors"
        {...rest}
      />
    </label>
  );
}
